import GlobalContext from '../contexts/GlobalContext';
import { useLocation } from 'react-router';
import { ELDFlags, EMixpanelEvent, maxEmailLength } from '../util/constants';
import { validatePassword } from '../util/validation/passwordValidation';
import { TCreateAccountPost, TRequestVerifyPost } from '../api/types';
import API from '../api/api';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { createContext, FC, useContext, useEffect, useState } from 'react';
import { validateEmail } from '../util/validation/formValidation';
import mixpanel from 'mixpanel-browser';

export enum EMigrateAccountUiState {
    default,
    routerParsed,
    ready,
    loading,
    notInitialized,
    error,
    success,
}

export type TMigrateAccountRouterState = {
    migrationUser: {
        firstName: string;
        lastName: string;
        email: string;
        countryCode: string;
        linkingToken: string;
    };
};

type TMigrateAccountContext = {
    migrateAccountUiState: EMigrateAccountUiState;
    setMigrateAccountUiState: (uiState: EMigrateAccountUiState) => void;
    firstName: string;
    setFirstName: (name: string) => void;
    lastName: string;
    setLastName: (name: string) => void;
    email: string;
    setEmail: (email: string) => void;
    emailIsValid: boolean;
    emailValidationMessage: string;
    password: string;
    setPassword: (password: string) => void;
    passwordIsValid: boolean;
    countryCode: string;
    setCountryCode: (countryCode: string) => void;
    countryIsValid: boolean;
    errorMessage: string;
    submitMigrateAccount: () => Promise<void>;
    submitVerifyPasscode: (passcode: string) => Promise<void>;
    resendVerifyPasscode: () => Promise<void>;
};

const MigrateAccountContext = createContext<TMigrateAccountContext | undefined>(undefined);

export const MigrateAccountProvider: FC = (props) => {
    const { children } = props;
    const {
        language,
        gatewayAPIBaseUrl,
        getCountriesList,
        countriesList,
        coBrandingChecked,
        getCoBranding,
        subscriberId,
    } = useContext(GlobalContext);
    const { state: routerState = {} as TMigrateAccountRouterState } = useLocation();
    const [migrateAccountUiState, setMigrateAccountUiState] = useState(EMigrateAccountUiState.default);
    const [identityUserId, setIdentityUserId] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [emailIsValid, setEmailIsValid] = useState(true);
    const [emailValidationMessage, setEmailValidationMessage] = useState(language.validation.email);
    const [password, setPassword] = useState('');
    const [passwordIsValid, setPasswordIsValid] = useState(true);
    const [countryCode, setCountryCode] = useState('');
    const [countryIsValid, setCountryIsValid] = useState(true);
    const [linkingToken, setLinkingToken] = useState('');
    const [errorMessage, setErrorMessage] = useState(language.validation.genericError);
    const { [ELDFlags.useConsumerPasscodeVerification]: usePasscodeForVerifiication } = useFlags();

    const parseRouterState = () => {
        try {
            const { migrationUser } = routerState as TMigrateAccountRouterState;
            const { firstName, lastName, email, countryCode, linkingToken } = migrationUser;
            setEmail(email);
            setFirstName(firstName);
            setLastName(lastName);
            countryCode && setCountryCode(countryCode);
            setLinkingToken(linkingToken);
            setMigrateAccountUiState(EMigrateAccountUiState.routerParsed);
        } catch (error) {
            console.error('Failed to parse router state', error);
            setMigrateAccountUiState(EMigrateAccountUiState.notInitialized);
        }
    };

    const getInitialData = async () => {
        !coBrandingChecked && (await getCoBranding());
        !countriesList.length && (await getCountriesList());
        setMigrateAccountUiState(EMigrateAccountUiState.ready);
    };

    const checkEmailIsValid = () => {
        let isValid = true;
        let validationMessage = '';
        if (!email.length) {
            isValid = false;
            validationMessage = language.validation.emailMissing;
        } else if (!validateEmail(email)) {
            isValid = false;
            validationMessage = language.validation.email;
        } else if (email.length > maxEmailLength) {
            isValid = false;
            validationMessage = language.validation.emailMaxLength;
        }
        setEmailIsValid(isValid);
        setEmailValidationMessage(validationMessage);
        return isValid;
    };

    const checkPasswordIsValid = () => {
        const isValid = validatePassword(password);
        setPasswordIsValid(isValid);
        return isValid;
    };

    const checkCountryIsValid = () => {
        const isValid = countryCode ? true : false;
        setCountryIsValid(isValid);
        return isValid;
    };

    const validateForm = () => {
        const emailIsValid = checkEmailIsValid();
        const passwordIsValid = checkPasswordIsValid();
        const countryIsValid = checkCountryIsValid();
        if (emailIsValid && passwordIsValid && countryIsValid) {
            return true;
        } else {
            return false;
        }
    };

    const submitMigrateAccount = async () => {
        const formIsValid = validateForm();
        if (formIsValid) {
            const postObject: TCreateAccountPost = {
                firstName,
                lastName,
                email,
                password,
                countryCode,
                linkingToken,
                ...(subscriberId && { subscriberId }),
                ...(usePasscodeForVerifiication && { usePasscode: true }),
            };
            try {
                setMigrateAccountUiState(EMigrateAccountUiState.loading);
                const response = await API.post.createAccount(gatewayAPIBaseUrl, postObject);
                const { id } = response.data;
                setIdentityUserId(id);
                API.post.trackEvent({ event: 'Sign In || Migration | Migration Complete' });
                mixpanel.identify(id);
                setMigrateAccountUiState(EMigrateAccountUiState.success);
            } catch (error) {
                if (error.response.status === 409) {
                    setErrorMessage(language.validation.accountExists.header);
                } else {
                    setErrorMessage(language.validation.genericError);
                }
                setMigrateAccountUiState(EMigrateAccountUiState.error);
            }
        }
    };

    const submitVerifyPasscode = async (passcode: string) => {
        const queryParams = `?code=${passcode}&subscriberId=${subscriberId}&userId=${identityUserId}`;
        try {
            await API.get.verifyAccount(queryParams);
            mixpanel.track(EMixpanelEvent.actionCompleted, { key: 'consumer_migration' });
        } catch (error) {
            console.error('Failed to verify passcode');
            throw error.response?.status;
        }
    };

    const resendVerifyPasscode = async () => {
        const postObject: TRequestVerifyPost = {
            email,
            userId: identityUserId,
            usePasscode: true,
            ...(subscriberId && { subscriberId }),
        };
        try {
            await API.post.requestVerify(gatewayAPIBaseUrl, postObject);
        } catch (error) {
            console.error('Failed to resend passcode');
            throw error.response?.status;
        }
    };

    useEffect(() => {
        switch (migrateAccountUiState) {
            case EMigrateAccountUiState.default:
                parseRouterState();
                break;
            case EMigrateAccountUiState.routerParsed:
                getInitialData();
                break;
            default:
                break;
        }
    }, [migrateAccountUiState]);

    return (
        <MigrateAccountContext.Provider
            value={{
                migrateAccountUiState,
                setMigrateAccountUiState,
                firstName,
                setFirstName,
                lastName,
                setLastName,
                email,
                setEmail,
                emailIsValid,
                emailValidationMessage,
                password,
                setPassword,
                countryCode,
                setCountryCode,
                countryIsValid,
                errorMessage,
                passwordIsValid,
                submitMigrateAccount,
                submitVerifyPasscode,
                resendVerifyPasscode,
            }}
        >
            {children}
        </MigrateAccountContext.Provider>
    );
};

export default MigrateAccountContext;
